import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Collapse,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';

// Status color mapping
const statusColors = {
  success: 'success',
  failure: 'error',
  warning: 'warning',
  info: 'info'
};

// Security level color mapping
const securityLevelColors = {
  public: 'success',
  internal: 'info',
  sensitive: 'warning',
  critical: 'error'
};

// Component for displaying a single log entry with expandable details
function LogEntry({ log, onViewDetails }) {
  const [expanded, setExpanded] = useState(false);
  
  const toggleExpand = () => {
    setExpanded(!expanded);
  };
  
  return (
    <>
      <TableRow 
        hover
        onClick={toggleExpand}
        sx={{ 
          cursor: 'pointer',
          '&:nth-of-type(odd)': { backgroundColor: 'action.hover' }
        }}
      >
        <TableCell>
          {format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss')}
        </TableCell>
        <TableCell>
          {log.actor.isSystem ? (
            <Chip size="small" label="System" color="secondary" />
          ) : log.actor.isAdmin ? (
            <Chip size="small" label={log.actor.username || 'Admin'} color="primary" />
          ) : (
            log.actor.username || 'User'
          )}
        </TableCell>
        <TableCell>{log.target.resource}</TableCell>
        <TableCell>{log.target.identifier || 'N/A'}</TableCell>
        <TableCell>{log.action.type}</TableCell>
        <TableCell>
          <Chip 
            size="small" 
            label={log.action.status} 
            color={statusColors[log.action.status] || 'default'} 
          />
        </TableCell>
        <TableCell>
          <IconButton size="small" onClick={(e) => {
            e.stopPropagation();
            onViewDetails(log);
          }}>
            <InfoIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      
      <TableRow>
        <TableCell colSpan={7} sx={{ p: expanded ? 2 : 0, border: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2 }}>
              <Typography variant="subtitle2" gutterBottom component="div">
                {log.action.description}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" component="div">
                    Security Level:&nbsp;
                    <Chip 
                      size="small" 
                      label={log.securityLevel} 
                      color={securityLevelColors[log.securityLevel] || 'default'}
                    />
                  </Typography>
                </Grid>
                
                {log.correlationId && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" component="div">
                      Correlation ID: {log.correlationId}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              
              {log.metadata && log.metadata.notes && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" component="div" color="text.secondary">
                    Notes: {log.metadata.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Detail modal for viewing complete log information
function LogDetailModal({ open, log, onClose }) {
  if (!log) return null;
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Audit Log Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">
              {log.action.description}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Actor</Typography>
            <Card variant="outlined" sx={{ mt: 1 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="body2">
                  {log.actor.isSystem ? 'System' : log.actor.username}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {log.actor.isSystem && <Chip size="small" label="System" color="secondary" />}
                  {log.actor.isAdmin && <Chip size="small" label="Admin" color="primary" />}
                </Stack>
                {log.actor.ipAddress && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    IP: {log.actor.ipAddress}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Target</Typography>
            <Card variant="outlined" sx={{ mt: 1 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="body2">
                  {log.target.resource} {log.target.identifier ? `(${log.target.identifier})` : ''}
                </Typography>
                {log.target.resourceId && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    ID: {log.target.resourceId}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Action</Typography>
            <Card variant="outlined" sx={{ mt: 1 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">{log.action.type}</Typography>
                  <Chip 
                    size="small" 
                    label={log.action.status} 
                    color={statusColors[log.action.status] || 'default'} 
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">Metadata</Typography>
            <Card variant="outlined" sx={{ mt: 1 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    {format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss')}
                  </Typography>
                  <Chip 
                    size="small" 
                    label={log.securityLevel} 
                    color={securityLevelColors[log.securityLevel] || 'default'} 
                  />
                </Stack>
                {log.correlationId && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Correlation ID: {log.correlationId}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {(log.metadata && (log.metadata.before || log.metadata.after || log.metadata.delta || log.metadata.notes)) && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Additional Details</Typography>
              <Card variant="outlined" sx={{ mt: 1 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {log.metadata.notes && (
                    <Typography variant="body2" paragraph>
                      {log.metadata.notes}
                    </Typography>
                  )}
                  
                  {log.metadata.delta && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" fontWeight="bold">Changes:</Typography>
                      <pre style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '8px', 
                        borderRadius: '4px',
                        overflowX: 'auto'
                      }}>
                        {JSON.stringify(log.metadata.delta, null, 2)}
                      </pre>
                    </Box>
                  )}
                  
                  {log.securityLevel !== 'sensitive' && log.securityLevel !== 'critical' && (
                    <>
                      {log.metadata.before && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" fontWeight="bold">Before:</Typography>
                          <pre style={{ 
                            backgroundColor: '#f5f5f5', 
                            padding: '8px', 
                            borderRadius: '4px',
                            overflowX: 'auto'
                          }}>
                            {JSON.stringify(log.metadata.before, null, 2)}
                          </pre>
                        </Box>
                      )}
                      
                      {log.metadata.after && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" fontWeight="bold">After:</Typography>
                          <pre style={{ 
                            backgroundColor: '#f5f5f5', 
                            padding: '8px', 
                            borderRadius: '4px',
                            overflowX: 'auto'
                          }}>
                            {JSON.stringify(log.metadata.after, null, 2)}
                          </pre>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// Retention policy dialog
function RetentionDialog({ open, onClose, onConfirm, isProcessing }) {
  const [days, setDays] = useState(90);
  
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Apply Retention Policy</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This action will permanently delete audit logs older than the specified number of days.
        </Alert>
        <TextField
          label="Days to keep"
          type="number"
          fullWidth
          value={days}
          onChange={(e) => setDays(e.target.value)}
          inputProps={{ min: 30, max: 365 }}
          helperText="Minimum: 30 days, Maximum: 365 days. Critical logs are never deleted."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isProcessing}>Cancel</Button>
        <Button 
          onClick={() => onConfirm(days)} 
          color="error" 
          variant="contained"
          disabled={isProcessing}
        >
          {isProcessing ? <CircularProgress size={24} /> : 'Apply Retention Policy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AuditLog() {
  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [retentionDialogOpen, setRetentionDialogOpen] = useState(false);
  const [isApplyingRetention, setIsApplyingRetention] = useState(false);
  const [retentionResult, setRetentionResult] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    actionType: '',
    resource: '',
    status: '',
    startDate: null,
    endDate: null,
    isSystem: '',
    isAdmin: '',
    securityLevel: ''
  });
  
  // Options for filter dropdowns
  const [filterOptions, setFilterOptions] = useState({
    actionTypes: [],
    resources: [],
    statuses: [],
    securityLevels: []
  });
  
  // Load logs from API
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = {
        page: page + 1, // API is 1-indexed
        limit: rowsPerPage,
        sortField: 'timestamp',
        sortDirection: 'desc'
      };
      
      // Add filters if they are set
      if (filters.search) params.search = filters.search;
      if (filters.actionType) params.actionType = filters.actionType;
      if (filters.resource) params.resource = filters.resource;
      if (filters.status) params.status = filters.status;
      if (filters.isSystem !== '') params.isSystem = filters.isSystem;
      if (filters.isAdmin !== '') params.isAdmin = filters.isAdmin;
      if (filters.securityLevel) params.securityLevel = filters.securityLevel;
      
      // Handle dates - now using standard date inputs
      if (filters.startDate) {
        params.startDate = filters.startDate;
      }
      
      if (filters.endDate) {
        params.endDate = filters.endDate;
      }
      
      const response = await axios.get('/api/admin/audit-logs', { params });
      
      if (response.data.status === 'success') {
        setLogs(response.data.data.logs);
        setTotal(response.data.data.pagination.total);
        setFilterOptions(response.data.data.filters);
      } else {
        throw new Error(response.data.message || 'Failed to fetch audit logs');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);
  
  // Load logs on mount and when dependencies change
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0); // Reset to first page when filters change
  };
  
  // Handle view details
  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };
  
  // Handle closing detail modal
  const handleCloseDetail = () => {
    setDetailModalOpen(false);
  };
  
  // Handle export
  const handleExport = async () => {
    try {
      // Build query parameters (same as fetch but without pagination)
      const params = {
        format: 'csv',
        sortField: 'timestamp',
        sortDirection: 'desc'
      };
      
      // Add filters if they are set
      if (filters.search) params.search = filters.search;
      if (filters.actionType) params.actionType = filters.actionType;
      if (filters.resource) params.resource = filters.resource;
      if (filters.status) params.status = filters.status;
      if (filters.isSystem !== '') params.isSystem = filters.isSystem;
      if (filters.isAdmin !== '') params.isAdmin = filters.isAdmin;
      if (filters.securityLevel) params.securityLevel = filters.securityLevel;
      
      // Handle dates - now using standard date inputs
      if (filters.startDate) {
        params.startDate = filters.startDate;
      }
      
      if (filters.endDate) {
        params.endDate = filters.endDate;
      }
      
      const response = await axios.get('/api/admin/audit-logs/export', {
        params,
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Failed to export audit logs');
    }
  };
  
  // Handle retention policy
  const handleApplyRetention = async (days) => {
    try {
      setIsApplyingRetention(true);
      setRetentionResult(null);
      
      const response = await axios.delete('/api/admin/audit-logs/retention', {
        params: { days }
      });
      
      if (response.data.status === 'success') {
        setRetentionResult({
          type: 'success',
          message: response.data.message
        });
        // Refresh logs after successful retention
        fetchLogs();
      } else {
        throw new Error(response.data.message || 'Failed to apply retention policy');
      }
    } catch (err) {
      setRetentionResult({
        type: 'error',
        message: err.message || 'Failed to apply retention policy'
      });
    } finally {
      setIsApplyingRetention(false);
      setRetentionDialogOpen(false);
    }
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      search: '',
      actionType: '',
      resource: '',
      status: '',
      startDate: null,
      endDate: null,
      isSystem: '',
      isAdmin: '',
      securityLevel: ''
    });
    setPage(0);
  };
  
  return (
    <Box>
      {/* Header with basic controls */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" component="h2">
          Audit Log
        </Typography>
        
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ minWidth: 120 }}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ minWidth: 120 }}
          >
            Export CSV
          </Button>
          
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setRetentionDialogOpen(true)}
            sx={{ minWidth: 120 }}
          >
            Retention Policy
          </Button>
        </Stack>
      </Stack>
      
      {/* Basic search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              placeholder="Search logs by description, user, or identifier"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={fetchLogs}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
              
              {Object.values(filters).some(v => v !== '' && v !== null) && (
                <Button
                  variant="outlined"
                  onClick={handleResetFilters}
                  startIcon={<CloseIcon />}
                >
                  Clear Filters
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Advanced filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Advanced Filters
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={filters.actionType}
                  onChange={(e) => handleFilterChange('actionType', e.target.value)}
                  label="Action Type"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.actionTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Resource</InputLabel>
                <Select
                  value={filters.resource}
                  onChange={(e) => handleFilterChange('resource', e.target.value)}
                  label="Resource"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.resources.map((resource) => (
                    <MenuItem key={resource} value={resource}>
                      {resource}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Security Level</InputLabel>
                <Select
                  value={filters.securityLevel}
                  onChange={(e) => handleFilterChange('securityLevel', e.target.value)}
                  label="Security Level"
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.securityLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Actor Type</InputLabel>
                <Select
                  value={filters.isSystem}
                  onChange={(e) => handleFilterChange('isSystem', e.target.value)}
                  label="Actor Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">System</MenuItem>
                  <MenuItem value="false">User</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Admin Action</InputLabel>
                <Select
                  value={filters.isAdmin}
                  onChange={(e) => handleFilterChange('isAdmin', e.target.value)}
                  label="Admin Action"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Admin Actions</MenuItem>
                  <MenuItem value="false">User Actions</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="End Date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
      
      {/* Results message */}
      {!loading && !error && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Found {total} audit log {total === 1 ? 'entry' : 'entries'}
          {Object.values(filters).some(v => v !== '' && v !== null) && ' matching your filters'}
        </Typography>
      )}
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Retention policy result message */}
      {retentionResult && (
        <Alert severity={retentionResult.type} sx={{ mb: 3 }}>
          {retentionResult.message}
        </Alert>
      )}
      
      {/* Main content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {logs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No audit logs found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Object.values(filters).some(v => v !== '' && v !== null)
                  ? 'Try adjusting your filters to see more results'
                  : 'The audit log is empty'}
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Actor</TableCell>
                    <TableCell>Resource</TableCell>
                    <TableCell>Identifier</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <LogEntry 
                      key={log._id} 
                      log={log} 
                      onViewDetails={handleViewDetails} 
                    />
                  ))}
                </TableBody>
              </Table>
              
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}
        </>
      )}
      
      {/* Detail modal */}
      <LogDetailModal
        open={detailModalOpen}
        log={selectedLog}
        onClose={handleCloseDetail}
      />
      
      {/* Retention dialog */}
      <RetentionDialog
        open={retentionDialogOpen}
        onClose={() => setRetentionDialogOpen(false)}
        onConfirm={handleApplyRetention}
        isProcessing={isApplyingRetention}
      />
    </Box>
  );
}

export default AuditLog; 